import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store';

import { useHydrate } from 'react-query';

// Layout
import AppLayout from './components/layout/AppLayout';

// Pages
import LoginPage     from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import MigrationPage from './pages/MigrationPage';
import MappingPage   from './pages/MappingPage';
import ExecutionPage from './pages/ExecutionPage';
import DiscoveryPage from './pages/Discovery';
import AssessPage from './pages/Assess';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import UsersPage from './pages/UsersPage';

import { PreviewPage, SettingsPage } from './pages/PreviewAndSettings';
import ConfigurationPage from './pages/ConfigurationPage';
import { useHydration } from './hooks/useHydration';

// ── Protected route wrapper ───────────────────────────────────────────────────
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated
    ? <AppLayout>{children}</AppLayout>
    : <Navigate to="/login" replace />;
}

export default function App() {

  // const hydrated = useHydration();

  // if (!hydrated) {
  //   return <div>Loading...</div>;
  // }

  const { isAuthenticated, refreshUser } = useAuthStore();

  // The logged-in user (including role) is cached in localStorage and
  // otherwise never updated — if an admin changes someone's role on the
  // backend, that user's already-open session would keep using the stale
  // role until a fresh login. Re-sync it from the backend on app load and
  // whenever the tab regains focus, so role changes (e.g. a promotion to
  // admin, which should unlock the Users module) take effect without
  // requiring the user to log out and back in.
  useEffect(() => {
    if (!isAuthenticated) return;

    refreshUser().catch(() => {
      // Non-fatal — keep using the cached user if the refresh fails
      // (e.g. transient network issue); existing session stays usable.
    });

    const handleFocus = () => {
      refreshUser().catch(() => {});
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isAuthenticated]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/dashboard"
        element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
      />

      <Route
        path="/migration"
        element={<ProtectedRoute><MigrationPage /></ProtectedRoute>}
      />
      
      <Route
        path="/configuration"
        element={<ProtectedRoute><ConfigurationPage /></ProtectedRoute>}
      />

      <Route
        path="/settings"
        element={<ProtectedRoute><SettingsPage /></ProtectedRoute>}
      />

      <Route
        path="/discovery"
        element={<ProtectedRoute><DiscoveryPage /></ProtectedRoute>}
      />

      <Route
        path="/assess"
        element={<ProtectedRoute><AssessPage /></ProtectedRoute>}
      />

      <Route
        path="/assess/:id"
        element={<ProtectedRoute><AssessPage /></ProtectedRoute>}
      />

      <Route
        path="/projects"
        element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>}
      />

      <Route
        path="/projects/:id"
        element={<ProtectedRoute name="page_details"><ProjectDetailPage /></ProtectedRoute>}
      />

      <Route
        path="/users"
        element={<ProtectedRoute><UsersPage /></ProtectedRoute>}
      />

      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
